import { memo, useMemo, useRef, useLayoutEffect, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useAnimation, AnimatePresence } from "framer-motion";

import useAuthStore from "../../store/useAuthStore";
import useLayoutStore from "../../store/useLayoutStore";
import useAppStore from "../../store/useAppStore";

const EmployeeCard = ({ employee, index }) => {
    const employeeState = useAppStore((state) => state.employeesState[employee.user_id]);
    const visibility = employeeState?.visibility
    const clientId = useAuthStore((state) => state.userId)

    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const isImgFallback = useRef(false)
    const imgRef = useRef()
    const cardContainerRef = useRef(null);
    const prevVisibilityRef = useRef(null)
    const enterRafRef = useRef(0)

    const imageUrl = useMemo(() => {
        const baseImgUrl = import.meta.env.VITE_API_IMAGES_URL;
        return `${baseImgUrl}/employees/${employee.image}`
    }, [])

    const controls = useAnimation();

    useLayoutEffect(() => {
        const container = cardContainerRef.current;
        if (!employeeState || !container) return;

        const wasVisible = prevVisibilityRef.current
        const isVisible = visibility === true
        prevVisibilityRef.current = isVisible

        if (enterRafRef.current) {
            cancelAnimationFrame(enterRafRef.current)
            enterRafRef.current = 0
        }

        const isGroupMoveEntrance = useAppStore
            .getState()
            .employeeGroupMovedUserIds.includes(String(employee.user_id));

        const staggerDelay = Math.min(0.015 * index, 0.18)

        // Prvi sync — samo uskladi display, bez animacije
        if (wasVisible === null) {
            if (isVisible) {
                container.style.display = "flex"
                controls.set({ scale: 1, opacity: 1 })
            }
            return
        }

        if (isVisible) {
            container.style.display = "flex";

            if (isGroupMoveEntrance) {
                controls.set({ scale: 0.4, opacity: 0 });
                controls.start({
                    scale: 1,
                    opacity: 1,
                    transition: { duration: 0.24, delay: staggerDelay, ease: "easeOut" },
                }).then(() => {
                    useAppStore.getState().clearEmployeeGroupMoveAnimation(employee.user_id);
                });
                return;
            }

            // Već vidljiva kartica (npr. update prisutnosti) — ne restartuj animaciju
            if (wasVisible === true) return

            controls.set({ scale: 0.4, opacity: 0 })

            // Sačekaj layout/paint pa tek onda animiraj (smanjuje seckanje pri "Svi zaposleni")
            enterRafRef.current = requestAnimationFrame(() => {
                enterRafRef.current = requestAnimationFrame(() => {
                    enterRafRef.current = 0
                    controls.start({
                        scale: 1,
                        opacity: 1,
                        transition: { duration: 0.22, delay: staggerDelay, ease: "easeOut" },
                    })
                })
            })
        } else {
            if (wasVisible === false) return

            controls.start({
                scale: 0.4,
                opacity: 0,
                transition: { duration: 0.16, ease: "easeIn" },
            }).then(() => {
                if (prevVisibilityRef.current === false && cardContainerRef.current) {
                    cardContainerRef.current.style.display = "none"
                }
            })
        }

        return () => {
            if (enterRafRef.current) {
                cancelAnimationFrame(enterRafRef.current)
                enterRafRef.current = 0
            }
        }
    }, [visibility, employee.user_id, index, controls, employeeState]);

    useLayoutEffect(() => {
        // if (isDarkTheme && isImgFallback.current)
        // imgRef.current.classList.add('dark-filter')

    }, [isDarkTheme])

    if (!employeeState) {
        return null;
    }

    return (
        <motion.div
            ref={cardContainerRef}
            className="pv-card-container"
            animate={controls}
            style={{ willChange: "transform, opacity" }}
        >
            <div className={`pv-card ${isDarkTheme ? 'dark-card' : 'white-card'}`}>
                <div className="pv-card-image">
                    <div>
                        <img
                            ref={imgRef}
                            src={imageUrl}
                            crossOrigin="use-credentials"
                            // className={`${isDarkTheme ? 'dark-shadow' : ''}`}
                            className={`${isDarkTheme ? 'dark-border' : ''}`}
                            onError={(e) => {
                                e.target.onerror = null; // sprečava loop
                                e.target.classList.add("pv-card-img-error"); // doda klasu
                                e.target.src = "/views/login.svg"; // fallback slika
                                isImgFallback.current = true
                            }}
                        />
                        {employeeState.currentActive && (<div className="pv-card-presence-signal"></div>)}
                    </div>
                </div>
                <div className="pv-card-content">
                    <h3>{employee.ime} {employee.prezime}</h3>
                    <EmployeePresenceInfo employeeState={employeeState} employeeId={employee.user_id} />
                </div>
                {!employeeState.currentActive &&
                    employeeState.visibility &&
                    employee.user_id !== clientId && (
                        <EmployeeNotifyButton employeeId={employee.user_id} />
                    )}
            </div>
        </motion.div>
    )
}

export default memo(EmployeeCard, (prev, next) =>
    prev.employee.user_id === next.employee.user_id
    && prev.employee.ime === next.employee.ime
    && prev.employee.prezime === next.employee.prezime
    && prev.employee.group === next.employee.group
    && prev.employee.image === next.employee.image
    && prev.index === next.index
);

const EmployeePresenceInfo = ({ employeeId, employeeState }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const { wasActiveToday, currentActive } = employeeState;

    const data = useMemo(() => {
        return useAppStore.getState().employeesData[employeeId];
    }, [employeeState, employeeId]);

    if (!wasActiveToday && !currentActive) {
        return (
            <div className={`pv-card-presence-inactive-container ${isDarkTheme ? 'dark-color' : ''}`}>
                <div>
                    Korisnik danas nije bio prisutan.
                </div>
            </div>
        );
    }


    return (
        <>
            {currentActive && data.presenceType && (
                <div className="pv-card-presence-type-container">
                    {/* <div className="pv-card-presence-type-icon"> */}
                    <img
                        src={data.presenceType === 'remote' ? "/sidebar/Ekran.svg" : "/sidebar/Office.svg"}
                        alt=""
                        className={isDarkTheme ? 'dark-filter' : ''}
                    />
                    {/* </div> */}
                    <div className="pv-card-presence-type-text">
                        {data.presenceType === 'remote' ? 'Online' : data.presenceLogs.locationRoom}
                    </div>
                </div>
            )}
            <div className="pv-card-presence-info-container">
                <EntryInfo time={data.presenceLogs.entry} />
                <PauseInfo duration={data.presenceLogs.pauseDuration} />
                <ExitInfo
                    isActive={currentActive}
                    time={currentActive ? data.presenceLogs.expectedExit : data.presenceLogs.exit}
                />
            </div>
        </>
    )
}

const EntryInfo = ({ time }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    return (
        <div className="pv-card-info-div">
            <div className="pv-card-info-img">
                <img src="/cards/VremeUlaska.svg" alt="" className={`${isDarkTheme ? 'dark-filter' : ''}`} />
            </div>
            <div className="pv-card-info-text">
                Vreme ulaska:
            </div>
            <div className="pv-card-info-value">
                {time}
            </div>
        </div>
    )
};

const PauseInfo = ({ duration }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    return (
        <div className="pv-card-info-div">
            <div className="pv-card-info-img">
                <img src="/cards/VremePauze.svg" alt="" className={`${isDarkTheme ? 'dark-filter' : ''}`} />
            </div>
            <div className="pv-card-info-text">
                Vreme pauze:
            </div>
            <div className="pv-card-info-value">
                {duration}min
            </div>
        </div>
    )
};

const ExitInfo = ({ isActive, time }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)
    const iconSrc = isActive ? "/cards/VremeKadIzlazi.svg" : "/cards/VremeIzlaska.svg"
    const text = isActive ? "Predviđen izlaz: " : "Vreme izlaska: "

    return (
        <div className="pv-card-info-div">
            <div className="pv-card-info-img">
                <img src={iconSrc} alt="" className={`${isDarkTheme ? 'dark-filter' : ''}`} />
            </div>
            <div className="pv-card-info-text">
                {text}
            </div>
            <div className="pv-card-info-value">
                {time}
            </div>
        </div>
    );
};

const EmployeeNotifyButton = ({ employeeId }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme)

    const activeClass = isDarkTheme ? 'dark-clicked' : 'clicked'

    const presenceNotification = useAppStore((state) => state.presenceNotifications[employeeId]);
    const setPresenceNotification = useAppStore((state) => state.setPresenceNotification)

    const [showMessage, setShowMessage] = useState(null); // "added" | "removed" | null
    const [initialized, setInitialized] = useState(false);

    const handleNotifyAbsence = () => {
        setPresenceNotification(employeeId)
    }

    useEffect(() => {
        if (!initialized) {
            // Prva inicijalizacija – ignoriši
            setInitialized(true);
            return;
        }
        if (presenceNotification === true) {
            setShowMessage("added");
        } else if (presenceNotification === false) {
            setShowMessage("removed");
        }
    }, [presenceNotification]);

    return (
        <>
            <div
                className="pv-card-notify-container"
                onClick={handleNotifyAbsence}
            >
                <svg
                    className={`pv-card-notify-icon ${isDarkTheme && 'dark-invert'} ${presenceNotification !== false ? activeClass : ''}`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 60 60"
                    aria-hidden
                >
                    <path fill="currentColor" d="M41.25,37.82h-22.48c-.59,0-1.1-.38-1.27-.94s.05-1.16.53-1.49c0,0,2.78-2.09,2.78-9.01,0-5.07,4.13-9.2,9.2-9.2s9.2,4.13,9.2,9.2c0,6.92,2.77,9.01,2.8,9.03.47.34.67.94.5,1.49-.18.55-.68.93-1.25.93ZM21.52,35.17h16.97c-.98-1.73-1.94-4.52-1.94-8.79,0-3.61-2.93-6.54-6.54-6.54s-6.54,2.93-6.54,6.54c0,4.27-.96,7.06-1.94,8.79Z" />
                    <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M31.64,40.36c0,.9-.74,1.64-1.64,1.64s-1.64-.74-1.64-1.64c0,0,0,0,0,0h3.28Z" />
                </svg>
            </div>
            <AnimatePresence>
                {showMessage && (
                    <NotifyModal showMessage={showMessage} onClose={() => setShowMessage(null)} />
                )}
            </AnimatePresence>
        </>
    )
}

const NotifyModal = ({ showMessage, onClose }) => {
    const isDarkTheme = useLayoutStore(state => state.isDarkTheme);
    const modalContainerRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (modalContainerRef.current && modalContainerRef.current.contains(e.target)) {
                return;
            }
            onClose();
        };
        document.addEventListener('mousedown', handleClickOutside, true);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [onClose]);

    const modalContent = (
        <>
            <motion.div
                className="dv-edit-modal-container"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0, delay: 0.14 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'none' }}
            >
                <motion.div
                    ref={modalContainerRef}
                    className={`pv-notify-modal ${isDarkTheme ? "dark-pv-notify-modal" : "white-pv-notify-modal"}`}
                    initial={{ opacity: 0, scale: 0.4 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.4 }}
                    transition={{ duration: 0.14, ease: "easeInOut" }}
                    style={{ pointerEvents: 'auto' }}
                >
                    {showMessage === "added" && (
                        <p>Obaveštenje je aktivirano – bićete obavešteni kada se zaposleni prijavi na posao.</p>
                    )}
                    {showMessage === "removed" && (
                        <p>Obaveštenje je deaktivirano – više nećete dobijati obaveštenja za ovog zaposlenog.</p>
                    )}
                    <button
                        onClick={onClose}
                        className={`pv-notify-close ${isDarkTheme ? "dark-big-btn" : ""}`}
                    >
                        OK
                    </button>
                </motion.div>
            </motion.div>
            <motion.div
                className={`profile-modal-bg-wrapper ${isDarkTheme ? "dark-profile-modal-bg-wrapper" : "white-profile-modal-bg-wrapper"}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 1 }}
                transition={{ duration: 0.14, ease: "easeInOut" }}
                style={{ pointerEvents: 'auto' }}
            >
            </motion.div>
        </>
    )

    return createPortal(modalContent, document.body);
}